import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMutation } from '@apollo/client';
import { GENERATE_INSTALL_LINK, POST_INSTALL_REDIRECT } from 'gql/mutations';
import { useAuthenticationStatus } from '@nhost/react';
import { ActionPage } from './NestedContainers/ActionPage';
import { useUserData } from '@nhost/react';
import { LoadingScreen } from 'components/LoadingScreen';
import { beginShopifyReauthorization, consumeShopifyReauthorizationReturnPath, isShopifyHost, SHOPIFY_REAUTH_RETURN_PATH_KEY } from 'utils/shopifyReauthorization';

export function PostInstall() {
  const [postInstallError, setPostInstallError] = useState('');
  const navigate = useNavigate();
  const user = useUserData();

  const finishPostInstall = (fallbackPath: string) => {
    const returnPath = consumeShopifyReauthorizationReturnPath(fallbackPath);
    window.location.assign(returnPath);
  };

  const [postInstallMutation, { called }] = useMutation(POST_INSTALL_REDIRECT, {
    onError: (error) => {
      if (error?.message?.toLowerCase() === '409_app_already_installed') {
        finishPostInstall('/dashboard');
      } else {
        setPostInstallError(
          error.message || 'An error occured. Please try again.',
        );
      }
      localStorage.removeItem('shipaid___post-install');
    },
    onCompleted: (data) => {
      const STORE_ID_KEY = `${user?.id}-shipaid___storeId`;
      const STORE_DOMAIN_KEY = `${user?.id}-shipaid___domain`;
      const STORE_NAME_KEY = `${user?.id}-shipaid___name`;

      localStorage.setItem(STORE_ID_KEY, data.handleInstallRedirect.id);
      localStorage.setItem(STORE_DOMAIN_KEY, data.handleInstallRedirect.store);
      localStorage.setItem(STORE_NAME_KEY, data.handleInstallRedirect.store);
      setTimeout(() => {
        // TODO: @stefan: We need a user friendly UI for the 7 second 5 wait
        localStorage.removeItem('shipaid___post-install');

        finishPostInstall('/');
      }, 5000);
    },
  });

  const [generateInstallLink, { called: installCalled }] = useMutation(GENERATE_INSTALL_LINK, {
    onCompleted: (data) => {
      try {
        const returnPath = sessionStorage.getItem(SHOPIFY_REAUTH_RETURN_PATH_KEY) || '/';
        beginShopifyReauthorization(data?.generateInstallLink, returnPath);
      } catch {
        setPostInstallError('Please restart the Shopify installation from the app listing.');
      }
    },
    onError: () => setPostInstallError('Please restart the Shopify installation from the app listing.'),
  });

  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuthenticationStatus();

  useEffect(() => {
    if (!called && !installCalled && isAuthenticated) {
      const searchParams = new URLSearchParams(location.search);
      const shop = searchParams.get('shop');
      if (!shop || !isShopifyHost(shop) || searchParams.getAll('shop').length !== 1) {
        setPostInstallError('Please restart the Shopify installation from the app listing.');
        return;
      }
      if (searchParams.get('install') === '1') {
        generateInstallLink({ variables: { store: shop } });
        return;
      }
      if (!searchParams.get('state')) {
        setPostInstallError('Please restart the Shopify installation from the app listing.');
        return;
      }
      postInstallMutation({
        variables: {
          code: searchParams.get('code') || undefined,
          store: shop,
          searchParams: Object.fromEntries(searchParams),
        },
      });
    } else if (!isLoading && !isAuthenticated) {
      console.warn('not authenticated, redirecting');
      const signupUrl = `/sign-up${location.search}`;
      localStorage.setItem('shipaid___post-install', window.location.href);
      window.location.href = signupUrl;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    called,
    installCalled,
    generateInstallLink,
    postInstallMutation,
    isAuthenticated,
    location.search,
    isLoading,
  ]);

  return (
    <div>
      {postInstallError ? (
        <ActionPage
          title="Oops!"
          subtitle={postInstallError}
          onClick={() => navigate('/dashboard')}
        />
      ) : (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <LoadingScreen />
          <div className="mt-8 max-w-md text-center px-4">
            <p className="text-lg font-semibold text-gray-900 mb-2">
              Please wait while we get your account set up and store connected.
            </p>
            <p className="text-sm text-gray-600">
              This could take a few minutes. Please DO NOT REFRESH...
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
